import { ApiError, apiErrorSchema, healthSchema, type Health } from '@molt/shared';
import { z } from 'zod';

/**
 * The API client.
 *
 * Two things it establishes, both cheap now and awkward later:
 *
 *  - Responses are parsed with the same Zod schema the server serialised them
 *    with (`@molt/shared`), so a contract change is a type error here rather
 *    than an undefined three components deep.
 *  - Failures arrive as the same `ApiError` the server throws. The client and
 *    the server agree on what an error *is*, which is what lets one component
 *    render every failure the same way.
 *
 * Requests are relative. Vite proxies `/api` to the server in development and
 * Caddy does it in production (§3.F, `00-` §7) — the client never knows a
 * second origin exists.
 *
 * ---------------------------------------------------------------------------
 * THE SESSION TRANSPORT — `FE-04.14`
 * ---------------------------------------------------------------------------
 * ⚠ The comment this file used to carry — *"Sessions will be cookie-based once
 * auth exists"* — has expired: authentication is real as of block **B06**
 * (Better Auth, `apps/server/src/auth/`), `x-molt-dev-user` is gone in every
 * environment, and the cookie is the only identity the server reads. What
 * replaces the promise is written out below, because every part of it is a
 * decision that looks like an omission from the outside:
 *
 *  1. **`credentials: 'same-origin'` is correct as it stands, and is not a
 *     placeholder.** Every URL built here is relative, so the browser resolves
 *     it against the page origin and `/api/…` **is** same-origin — through the
 *     Vite proxy in development and through Caddy in production alike. The
 *     cookie therefore rides on every request, and `'include'` would buy
 *     nothing except the ability to send it to an origin this client is
 *     deliberately unable to name.
 *  2. **It is applied after the caller's `init` spread, so a caller cannot
 *     drop it.** A request that quietly omits the session is a 401 that looks
 *     like an authorisation bug; there is no legitimate reason for a call site
 *     to opt out, so the option is not offered.
 *  3. **Nothing here sets an `Origin` header, and nothing should.** Better
 *     Auth's `originCheckMiddleware` is the CSRF defence and it refuses a
 *     cookie-bearing request whose `Origin` is not on `trustedOrigins`
 *     (`apps/server/src/auth/better-auth.ts`). `Origin` is a forbidden header
 *     name: the browser sets it and script cannot. It arrives as the page
 *     origin — the one the dev server serves and `WEB_ORIGIN` names — so a
 *     browser is already correct and an explicit header would be silently
 *     dropped. (The rule is a live trap for **curl**, which sends no `Origin`
 *     at all and is refused, not for `fetch`.)
 *  4. **A 401 surfaces as exactly one condition**, `isUnauthenticated` below,
 *     so the router binds its redirect in one place rather than at forty call
 *     sites. See its doc comment.
 */

const BASE = '/api';

const API_ERROR_FALLBACK_MESSAGES = {
  'primitives.apiError.network': 'Could not reach the server',
  'primitives.apiError.unexpectedResponse': 'Unexpected {status} response',
  'primitives.apiError.unexpectedShape': 'The server sent an unexpected shape for {path}',
} as const;

type ApiErrorMessageKey = keyof typeof API_ERROR_FALLBACK_MESSAGES;

export type ApiErrorMessageFormatter = (
  key: ApiErrorMessageKey,
  values?: Record<string, string | number>,
) => string;

let apiErrorMessageFormatter: ApiErrorMessageFormatter = sourceApiErrorMessage;

export function configureApiErrorMessages(
  formatter: ApiErrorMessageFormatter | null | undefined,
): void {
  apiErrorMessageFormatter = formatter ?? sourceApiErrorMessage;
}

function apiErrorMessage(
  key: ApiErrorMessageKey,
  values?: Record<string, string | number>,
): string {
  return apiErrorMessageFormatter(key, values);
}

function sourceApiErrorMessage(
  key: ApiErrorMessageKey,
  values: Record<string, string | number> = {},
): string {
  return API_ERROR_FALLBACK_MESSAGES[key].replace(
    /\{([A-Za-z0-9_]+)\}/g,
    (_match, slot: string) => {
      const value = values[slot];
      if (value === undefined) {
        throw new Error(`API error message needs a value for {${slot}}`);
      }
      return String(value);
    },
  );
}

export async function request<S extends z.ZodType>(
  path: string,
  schema: S,
  init?: RequestInit,
): Promise<z.infer<S>> {
  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        /*
         * ⚠ ONLY WHEN THERE IS A BODY, and this is a bug `C2` found rather
         * than a style choice.
         *
         * This header used to be unconditional. Fastify refuses a request that
         * declares `content-type: application/json` and then sends nothing —
         * `FST_ERR_CTP_EMPTY_JSON_BODY`, a 400 — so **every bodyless POST
         * failed**, and the product had none until `C2`: `POST
         * /lists/:id/archive` takes no body, and neither will
         * `POST /lists/:id/restore` (chunk `F3`) or any other lifecycle route
         * that spells its verb in the path rather than in a payload.
         *
         * It surfaced as an archive that simply did not happen — the
         * optimistic write removed the column, the request 400'd, the rollback
         * put it back, and the whole round trip was fast enough to look like
         * nothing had been clicked. No test in `apps/web` could see it: unit
         * tests mock this module, and this was the first e2e assertion in the
         * repository to drive a bodyless write through a browser.
         */
        ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...init?.headers,
      },
      // The session cookie, on every request, not overridable by the caller —
      // see "THE SESSION TRANSPORT" at the top of this file for why this line
      // is both sufficient and deliberately last.
      credentials: 'same-origin',
    });
  } catch (cause) {
    // Network-level: the server is down, or the browser is offline.
    throw new ApiError('internal', apiErrorMessage('primitives.apiError.network'), {
      network: [cause instanceof Error ? cause.message : String(cause)],
    });
  }

  const text = await response.text();
  const json: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(json);
    if (parsed.success) {
      throw new ApiError(
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.details,
      );
    }
    // A non-JSON error body means something in front of the app answered —
    // a proxy, a gateway — not the API.
    throw new ApiError(
      'internal',
      apiErrorMessage('primitives.apiError.unexpectedResponse', {
        status: response.status,
      }),
    );
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'internal',
      apiErrorMessage('primitives.apiError.unexpectedShape', { path }),
      {
        [path]: parsed.error.issues.map(
          (issue) => `${issue.path.join('.')}: ${issue.message}`,
        ),
      },
    );
  }

  return parsed.data;
}

/**
 * The verb surface — `FE-04.13`.
 *
 * These are **not** the per-endpoint functions; each of those ships with the
 * block that needs it. They are the four spellings of `request` a block would
 * otherwise write out longhand, and the reason to have them is uniformity
 * rather than brevity: a body serialised by hand is a body somebody eventually
 * forgets to `JSON.stringify`, or sends without the `content-type` the server's
 * parser requires, and the failure is a 400 about a malformed body rather than
 * about the missing header.
 *
 * A block's endpoint function is one line on top of one of these:
 *
 *     export const getBoardContents = (id: string): Promise<BoardContents> =>
 *       get(`/boards/${id}/contents`, boardContentsSchema);
 *
 * The path is the block's to spell — this file does not enumerate routes, for
 * the same reason `@molt/shared` does not: a route added here without the block
 * that serves it is unreviewable.
 */

/** `GET`. First intended caller: every read hook, from `FE-06.1` onward. */
export const get = <S extends z.ZodType>(
  path: string,
  schema: S,
  init?: Omit<RequestInit, 'method' | 'body'>,
): Promise<z.infer<S>> => request(path, schema, { ...init, method: 'GET' });

/** `POST`. First intended caller: `FE-12.3`'s card creation. */
export const post = <S extends z.ZodType>(
  path: string,
  schema: S,
  body?: unknown,
  init?: Omit<RequestInit, 'method' | 'body'>,
): Promise<z.infer<S>> => request(path, schema, withBody('POST', body, init));

/**
 * `PATCH`. First intended caller: `FE-12.3`'s card rename.
 *
 * The verb the product mostly writes with: `SC-4` is one route per *field*, so
 * a whole-object `PUT` is the shape this codebase deliberately does not have.
 */
export const patch = <S extends z.ZodType>(
  path: string,
  schema: S,
  body?: unknown,
  init?: Omit<RequestInit, 'method' | 'body'>,
): Promise<z.infer<S>> => request(path, schema, withBody('PATCH', body, init));

/** `PUT`. Used only by routes whose contract is explicitly whole-value. */
export const put = <S extends z.ZodType>(
  path: string,
  schema: S,
  body?: unknown,
  init?: Omit<RequestInit, 'method' | 'body'>,
): Promise<z.infer<S>> => request(path, schema, withBody('PUT', body, init));

/**
 * `DELETE`, spelled `del` because `delete` is reserved.
 *
 * Pass `emptyResponseSchema` for the routes that answer `204`.
 */
export const del = <S extends z.ZodType>(
  path: string,
  schema: S,
  init?: Omit<RequestInit, 'method' | 'body'>,
): Promise<z.infer<S>> => request(path, schema, { ...init, method: 'DELETE' });

function withBody(
  method: string,
  body: unknown,
  init: Omit<RequestInit, 'method' | 'body'> | undefined,
): RequestInit {
  return {
    ...init,
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  };
}

/**
 * What a `204 No Content` route returns once `request` has read its empty body.
 *
 * `request` turns an empty response body into `null` rather than `undefined`,
 * so this is `z.null()` and not `z.void()` — a mismatch there would surface as
 * "the server sent an unexpected shape", which is a confusing way to learn that
 * a delete succeeded.
 */
export const emptyResponseSchema = z.null();

/**
 * The session condition — `FE-04.14`.
 *
 * ---------------------------------------------------------------------------
 * ONE CONDITION, ONE PLACE, BECAUSE THE ALTERNATIVE IS FORTY STRING LITERALS
 * ---------------------------------------------------------------------------
 * Every route that needs an account calls `requireUser`
 * (`apps/server/src/auth/guard.ts`), which throws the shared `unauthenticated`
 * `ApiError`; `request` above rethrows it verbatim. So "the session is gone" is
 * already one fact on the wire — what this adds is one *name* for it, so that
 * the string `'unauthenticated'` is compared in exactly one file.
 *
 * `ERROR_CODES` in `@molt/shared` spells it **`'unauthenticated'`** — not
 * `'unauthorized'`, not `'unauthorised'`, and not HTTP's own name for the 401
 * status, which is `Unauthorized` and means this. Three plausible spellings,
 * one correct one, and a wrong guess is a redirect that silently never fires.
 *
 * **First intended caller: `FE-06.1`**, which owns the sign-in surface and the
 * unauthenticated redirect that preserves the requested URL. It binds that
 * redirect **once** — in a router `errorComponent` or a `QueryCache`
 * `onError` — as `if (isUnauthenticated(error)) …`. Nothing else in the client
 * should compare an error code to decide whether somebody is signed in.
 *
 * ⚠ Distinguish this from `'forbidden'` and `'not_found'`, which are *not*
 * session conditions and must not redirect: a member who may not see a board
 * gets `404` by design, so that the route is not an existence oracle
 * (`CLAUDE.md`, "A caller outside a read scope gets 404"). Redirecting those to
 * sign-in would tell a signed-in member to sign in again, repeatedly.
 */
export type UnauthenticatedError = ApiError & { readonly code: 'unauthenticated' };

/** The predicate. Narrow with this; never compare the code string yourself. */
export function isUnauthenticated(error: unknown): error is UnauthenticatedError {
  return error instanceof ApiError && error.code === 'unauthenticated';
}

/**
 * Whether a failed request is worth attempting again — `FE-04.14`.
 *
 * ✅ **Wired.** `apps/web/src/main.tsx`'s `QueryClient` sets
 * `defaultOptions.queries.retry: shouldRetryRequest`, and this paragraph used
 * to be the handover note asking for it — it outlived the handover by two
 * blocks and was corrected in place by `C1` rather than deleted, because the
 * failure it described is worth keeping: a global `retry: 1` retries *a 401*,
 * the session is no more present on the second attempt than the first, and the
 * only effect is that every signed-out screen makes two round trips and
 * `FE-06.1`'s redirect fires one request later than it should.
 *
 * The rule: retry once, and only what a second attempt could plausibly fix. A
 * `4xx` is the client being wrong about something and will be wrong again;
 * `internal` covers both a real `500` and the network failure `request` maps
 * onto it, and those are the ones that come good on their own.
 */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  if (!(error instanceof ApiError)) return false;
  return error.code === 'internal' || error.code === 'rate_limited';
}

export const api = {
  health: (): Promise<Health> => request('/health', healthSchema),
};
